import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {Util} from "../../util/Util";
import {AddressValidationOrder} from "../../../model/AddressValidationOrder";
import {SimpleOrder} from "../csv/csv-page/SimpleOrder";
import {AddressValidationService, ValidationAddress} from "../../service-data/address-validation.service";
import {StartupService} from "../../service-ui/startup.service";

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.css']
})
export class AddressComponent implements OnInit, OnChanges {
  @Input() addressValidationOrder: AddressValidationOrder;

  protected readonly Util = Util;

  constructor(private addressValidationService: AddressValidationService, private startupService: StartupService) {
  }

  /**
   * May THIS operator ship an address the validator would not confirm?
   *
   * Read from the session rather than passed in, so every screen embedding this
   * component gets the same answer and none of them can forget to pass it.
   * See `StartupService.canOverrideAddressValidation()` for why it is not
   * simply `isNetwork()`.
   */
  get canOverrideAddressValidation (): boolean {
    return this.startupService.canOverrideAddressValidation();
  }

  /**
   * Show the override controls only where they are both PERMITTED and
   * SENSIBLE. The second half matters as much as the first: offering "use it
   * anyway" before anyone has asked the validator would turn the override into
   * the ordinary way to save an address.
   */
  canOfferUnvalidatedOverride (): boolean {
    return this.canOverrideAddressValidation
      && !!this.addressValidationOrder
      && this.addressValidationOrder.canOverrideBeOffered();
  }

  /**
   * Take responsibility for the address currently selected.
   *
   * Re-checks the permission rather than trusting that the template's `*ngIf`
   * was the only way in -- the model records the decision but does not
   * authorise it, so this is where authorisation has to happen.
   */
  acceptUnvalidatedAddress (addressValidationOrder: AddressValidationOrder) {
    if ( this.canOverrideAddressValidation && addressValidationOrder.canOverrideBeOffered() ) {
      addressValidationOrder.acceptUnvalidatedAddress();
    }
  }

  /**
   * Select the edited address AND accept it unvalidated, in one action.
   *
   * These were two steps and operators reliably did only the first, leaving a
   * selected address that the gate still refused with no obvious reason why.
   * The accept is skipped where it is not needed -- an unvalidatable country
   * already passes the gate without one.
   */
  useEditedAddressWithoutValidation (addressValidationOrder: AddressValidationOrder) {
    addressValidationOrder.useEditedAddress();
    this.acceptUnvalidatedAddress(addressValidationOrder);
  }


  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  /**
   * Ask the validator about the edited address.
   *
   * The four-deep nest of identical retries this replaces was trying to work
   * round `validate()` emitting `undefined` on failure. It could not succeed:
   * after the fourth attempt it called `console.log` and stopped, so a
   * persistent failure left the panel looking exactly as it did before the
   * button was pressed -- no spinner, no message, no result. The operator's only
   * evidence that anything had happened was that nothing had.
   *
   * `validate()` now retries internally and always emits a result, so the
   * outcome -- including "the call failed" -- lands on the order where the
   * template can render it.
   */
  validateEditedAddress (addressValidationOrder: AddressValidationOrder) {
    const address = new ValidationAddress(undefined);
    address.address1 = addressValidationOrder.editedAddress1;
    address.address2 = addressValidationOrder.editedAddress2;
    address.city = addressValidationOrder.editedCity;
    address.state = addressValidationOrder.editedRegion;
    address.country = addressValidationOrder.editedCountry;
    address.postalcode = addressValidationOrder.editedPostalCode;

    // Set before the call goes out, so the "waiting" state is visible for the
    // whole of the round trip rather than only after it returns.
    addressValidationOrder.markAddressValidationRequested();

    this.addressValidationService.validate(address).subscribe(
      addressValidationResult => {
        if ( addressValidationResult ) {
          addressValidationOrder.setAddressValidationResult(addressValidationResult);
        } else {
          // Defensive: the service contract says this cannot happen. If it ever
          // does, "we do not know" is the honest reading -- never "not a real
          // address".
          addressValidationOrder.setAddressValidationError('The address validation service returned no response');
        }
      },
      error => {
        addressValidationOrder.setAddressValidationError(error && error.message ? error.message : 'The address validation request failed');
      }
    );
  }

}
